// Ranged keyword. Pure. No React, no Three.js.
//
// ── Codex (Ranged / Ranged X) ──
//
//   "Ranged is a keyword that grants a unit the following ACTIVATED
//   ability: Tap → Shoot a projectile that stops after 1 step. Strike
//   the hit unit. If a unit has Ranged X ... replace '1 step' with
//   'X steps.' ... The ranged projectile follows normal projectile
//   rules. If the projectile hits a unit, the ranged shooter strikes
//   the hit unit. Note that the ranged ability shoots a projectile
//   that may hit a unit; it will not hit sites, and thus cannot damage
//   sites in that way (use the Move and Attack ability instead)."
//
// ── Codex (Projectile) ──
//
//   "A projectile's flight begins at the shooting unit's location and
//   travels within the same region in a CARDINAL direction. Its flight
//   continues until it either reaches the edge of its region or it
//   reaches the first unit (enemy or ally) along its path, ignoring
//   any allies at the projectile's starting location. If there are
//   multiple valid units that could be hit, the projectile's controller
//   chooses which unit the projectile hits. Projectiles cannot hit
//   minions with Stealth."
//
// ── Model ──
//
//   • Shooting is a SEPARATE activated ability ("Shoot"), not part of
//     Move and Attack. The attack envelope is the unit's own location
//     only; Ranged never widens it.
//   • `getProjectileHits` walks the four cardinal lines up to range
//     steps and returns the first hittable unit per direction.
//     that's the candidate set the shooter's controller picks from.
//   • Stealth minions can't be hit and don't block the flight (they
//     can't interact with the projectile). Sites never block or get
//     hit. Subsurface units are in a different region.
//   • A shot strikes the hit unit with no strike-back, no defend
//     window, and no intercept (it isn't an attack).
//   • The N value is hydrated onto `cardInstance._rangedValue` from
//     the catalog (extractRangedValue in normalizeCards). 0 for units
//     without the keyword. Aura-granted Ranged without a value
//     defaults to 2 until the aura-with-value model lands.

const RANGED = 'ranged';

import { hasActiveStealth } from './stealth';
import { isCardSilenced } from './keywordQuery';
import { isDisabled } from '../automation/scope';
import { countsAsUnit } from './typeline';

const CARDINAL_DIRECTIONS = [
  { name: 'S', dc:  0, dr:  1 },
  { name: 'N', dc:  0, dr: -1 },
  { name: 'E', dc:  1, dr:  0 },
  { name: 'W', dc: -1, dr:  0 },
];

/**
 * THE canonical projectile flight walker (codex Projectile). Every
 * projectile shooter. Ranged units, Flamecaller's fire blast, Avatar
 * of Fire's Fireball. Routes through this one function; the only
 * variation between them is `maxSteps` (Ranged N stops after N steps,
 * the avatar projectiles fly to the region edge).
 *
 * Per cardinal direction with a hittable unit in line of sight,
 * returns:
 *   {
 *     direction: 'N' | 'S' | 'E' | 'W',
 *     pathCells: Array<{col,row}>,  // every cell the projectile crosses,
 *                                   // ending with the impact cell
 *     impactCell: {col,row},        // the cell the projectile lands on
 *   }
 * Directions where the projectile fizzles (reaches the grid edge, or
 * exhausts maxSteps, without a hittable unit) are omitted. The
 * shooter's start cell is NOT in pathCells per codex ("Projectiles
 * enter their starting location" marks t=0; pathCells is the t>0
 * flight).
 *
 * `unitsByCell` is a Map<"col,row", cardInstance[]> of every unit that
 * BLOCKS or can be HIT in the shooter's region; `isHittable` decides
 * impact (Stealth units are not hittable AND don't block. Leave them
 * out of unitsByCell or let isHittable reject them; both walk on).
 */
export function projectilePaths({
  startCell,
  totalCols,
  totalRows,
  unitsByCell,
  isHittable,
  maxSteps = Infinity,
  canTravelTo = () => true,
  canHitAtOrigin = () => false,
}) {
  if (!startCell || !unitsByCell || typeof isHittable !== 'function') return [];
  const originHits = (unitsByCell.get(`${startCell.col},${startCell.row}`) || []).filter((u) => isHittable(u) && canHitAtOrigin(u));
  if (originHits.length) return [{ direction: 'here', pathCells: [{ ...startCell }], impactCell: { ...startCell } }];
  const out = [];
  for (const { name, dc, dr } of CARDINAL_DIRECTIONS) {
    let col = startCell.col + dc;
    let row = startCell.row + dr;
    const pathCells = [];
    let impactCell = null;
    let steps = 0;
    while (col >= 0 && row >= 0 && col < totalCols && row < totalRows && steps < maxSteps) {
      if (!canTravelTo(col, row)) break;
      steps += 1;
      pathCells.push({ col, row });
      const here = unitsByCell.get(`${col},${row}`) || [];
      const hit = here.find((u) => isHittable(u));
      if (hit) { impactCell = { col, row }; break; }
      col += dc;
      row += dr;
    }
    if (impactCell) out.push({ direction: name, pathCells, impactCell });
  }
  return out;
}

// Units (Minion / Avatar) at (col,row) in the same region as the shooter.
// Sites are not units; stealth minions can't be hit (and don't block).
export function hittableUnitsAt(shooter, col, row, meshes, cellAt) {
  const region = shooter.subsurface || null;
  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.id === shooter.id) continue;
    if (card.isSite || !countsAsUnit(card)) continue;
    if ((card.subsurface || null) !== region) continue;
    if (hasActiveStealth(card)) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || cell.col !== col || cell.row !== row) continue;
    out.push(card);
  }
  return out;
}

/**
 * Candidate hits for a Shoot activation. Walks the four cardinal lines
 * up to `range` steps from the shooter's cell; each line stops at the
 * first unit (enemy or ally). Allies at the starting location are
 * ignored; an ENEMY at the starting location is the first unit on every
 * path, so it becomes the only candidate.
 *
 * @returns Array<{ card, cell:{col,row}, distance }>
 */
export function getProjectileHits(shooter, fromCell, { meshes, cellAt, totalCols, totalRows }) {
  if (!shooter || !fromCell) return [];
  const range = getRangedValue(shooter);
  if (range <= 0) return [];
  const shooterRotated = !!shooter.rotated;

  // Starting location: allies ignored, enemies stop the projectile.
  const atStart = hittableUnitsAt(shooter, fromCell.col, fromCell.row, meshes, cellAt)
    .filter((c) => !!c.rotated !== shooterRotated);
  if (atStart.length) {
    return atStart.map((card) => ({ card, cell: { col: fromCell.col, row: fromCell.row }, distance: 0 }));
  }

  // One flight walk for every projectile in the game: route through the
  // canonical walker with maxSteps = range. The cell map is pre-filtered
  // (region / stealth / unit-type / not-shooter) so every listed unit is
  // hittable; multiple units at the impact cell are all valid hits
  // (controller chooses).
  const unitsByCell = new Map();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.id === shooter.id) continue;
    if (card.isSite || !countsAsUnit(card)) continue;
    if ((card.subsurface || null) !== (shooter.subsurface || null)) continue;
    if (hasActiveStealth(card)) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    const key = `${cell.col},${cell.row}`;
    const list = unitsByCell.get(key) || [];
    list.push(card);
    unitsByCell.set(key, list);
  }
  const paths = projectilePaths({
    startCell: fromCell, totalCols, totalRows, unitsByCell,
    isHittable: () => true, maxSteps: range,
  });
  const out = [];
  const seen = new Set();
  for (const path of paths) {
    const units = unitsByCell.get(`${path.impactCell.col},${path.impactCell.row}`) || [];
    for (const card of units) {
      if (seen.has(card.id)) continue;
      seen.add(card.id);
      out.push({ card, cell: { ...path.impactCell }, distance: path.pathCells.length });
    }
  }
  return out;
}

/**
 * Effective Ranged N value for a card. Reads `_rangedValue` (printed,
 * hydrated by addCardToTable). Returns 0 when the keyword is absent
 * or the value is missing.
 *
 * Aura-granted Ranged is not yet modelled. When the keyword shows up
 * in `_auraStatuses` we know SOMETHING granted it but have no value,
 * so we default to range 2 as the conservative "you can shoot a bit
 * further than melee" choice. Aura-Ranged is rare enough today (one
 * artifact) that this is acceptable until the aura-with-value model
 * lands.
 */
export function getRangedValue(card) {
  if (!card) return 0;
  // Disabled / silenced units lose Ranged like any other keyword
  // ability (codex), so they can't shoot until the state clears.
  if (isDisabled(card) || isCardSilenced(card)) return 0;
  const printed = Number(card._rangedValue || 0);
  if (printed > 0) return Math.max(0, printed + (card._auraRangeDelta || 0));
  const auraGrant = (card._auraStatuses || []).includes(RANGED)
    || (card.statuses || []).includes(RANGED);
  return auraGrant ? Math.max(0, 2 + (card._auraRangeDelta || 0)) : 0;
}

/** True iff the card has Ranged with effective value ≥ 1. */
export function hasRanged(card) {
  return getRangedValue(card) > 0;
}

/**
 * Chebyshev distance between two cells. King-move metric. Diagonal
 * counts as distance 1. Returns Infinity when either cell is invalid.
 */
export function chebyshevDistance(a, b) {
  if (!a || !b) return Infinity;
  if (a.col == null || a.row == null || b.col == null || b.row == null) return Infinity;
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

/**
 * Manhattan distance. Taxi-cab metric used to decide melee envelope.
 * Same metric as the baseline `getAttackableCells` (same cell + 4
 * cardinal adjacent are exactly the cells with Manhattan ≤ 1).
 */
export function manhattanDistance(a, b) {
  if (!a || !b) return Infinity;
  if (a.col == null || a.row == null || b.col == null || b.row == null) return Infinity;
  return Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
}

/**
 * True when the attack from `attackerCell` to `defenderCell` qualifies
 * as a projectile attack. I.e. the attacker shot from outside melee
 * range. Used by combat to suppress defender strike-back.
 *
 * "Melee range" here means Manhattan ≤ 1 (matches getAttackableCells's
 * baseline envelope). A Ranged attacker engaging an adjacent foe trades
 * blows normally; only shots from 2+ cells away skip the counter.
 */
export function isProjectileAttack(attackerCell, defenderCell) {
  return manhattanDistance(attackerCell, defenderCell) > 1;
}

/**
 * All grid cells within Chebyshev radius `range` of (fromCol, fromRow),
 * clamped to grid bounds. Used by combat to enumerate Ranged-eligible
 * cells. Returns the source cell at distance 0 too.
 *
 * Range 1 still covers all 8 king-move neighbours plus the source.
 * note that's wider than melee (which is cardinal-only). Ranged 1
 * doesn't really exist in the printed catalog (Ranged 2 is the
 * minimum we've seen), so this distinction is academic. The combat
 * caller stitches the melee envelope union with the Ranged envelope
 * so adjacent-cardinal stays valid regardless.
 */
export function getRangedAttackableCells(fromCol, fromRow, totalCols, totalRows, range) {
  const r = Math.max(0, Math.floor(Number(range) || 0));
  const out = [];
  for (let dc = -r; dc <= r; dc++) {
    for (let dr = -r; dr <= r; dr++) {
      const c = fromCol + dc;
      const row = fromRow + dr;
      if (c < 0 || row < 0 || c >= totalCols || row >= totalRows) continue;
      out.push({ col: c, row });
    }
  }
  return out;
}
